// src/pages/ccp/BakingCcpStartPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import bakingCcpService from '../../services/bakingCcpService';
import sqfService from '../../services/sqfService';
import logService from '../../services/logService';
import ingredientService from '../../services/ingredientService';
import productService from '../../services/productService';
import { usePermissions } from '../../context/PermissionContext';
import { generateLotCode } from '../../utils/lotCodeUtils';
import { useLanguage, t } from '../../context/LanguageContext';
import { getCcpFlowLabels } from '../../config/ccpFlowLabels';

const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const nowLocalTimeHHMM = () => {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${min}`;
};

// productionDate: "YYYY-MM-DD", timeHHMM: "HH:mm" -> ISO string
const combineDateAndTimeToISO = (productionDate, timeHHMM) => {
  if (!productionDate || !timeHHMM) return null;
  const dtLocal = `${productionDate}T${timeHHMM}`;
  const d = new Date(dtLocal);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

export default function BakingCcpStartPage() {
  const navigate = useNavigate();
  const { companySettings } = usePermissions();
  const { lang } = useLanguage();
  const lotCodeFormat = companySettings?.lotCodeFormat || null;

  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [gateBlocking, setGateBlocking] = useState(null); // pending items from prepare, or null

  const [products, setProducts] = useState([]);
  const [allIngredients, setAllIngredients] = useState([]);
  const [flowType, setFlowType] = useState('baking');
  const L = getCcpFlowLabels(flowType);

  const ingredientById = useMemo(() => {
    const m = new Map();
    (allIngredients || []).forEach(i => m.set(Number(i.id), i));
    return m;
  }, [allIngredients]);

  // Multi-product selection
  const [entries, setEntries] = useState([{ productId: '', productName: '' }]);
  const [productSuggestions, setProductSuggestions] = useState({});

  // Cache recipes
  const cachedRecipesRef = useRef({});

  // Ingredient lot codes
  const [ingredientEntries, setIngredientEntries] = useState([]);

  const [showMissingLotsModal, setShowMissingLotsModal] = useState(false);

  const [form, setForm] = useState({
    lotCode: generateLotCode(lotCodeFormat),
    productionDate: todayISO(),
    productionStartTime: nowLocalTimeHHMM(),
    ovenTempStartF: '',
  });

  // Existing-lot modal
  const [showExistingLotModal, setShowExistingLotModal] = useState(false);
  const [existingLotInfo, setExistingLotInfo] = useState({
    message: '',
    runId: null,
    runStatus: '',
    lotCode: '',
  });

  // Covers both flow types — "in progress" means there's a live run to go back to, vs a
  // completed run that should route to the SQF report instead.
  const isRunInProgress = (status) =>
    ['BAKING', 'BAKING_PAUSED', 'PASTEURIZING', 'PASTEURIZING_PAUSED', 'AGING', 'READY_TO_CHURN'].includes(String(status || ''));

  // If active run exists, redirect immediately
  useEffect(() => {
    let cancelled = false;

    const redirectIfActive = async () => {
      try {
        const res = await bakingCcpService.getActiveRun();
        const run = res?.run || null;
        if (!cancelled && run?.id) {
          navigate(`/ccp/baking/live/${run.id}`, { replace: true });
        }
      } catch (e) {
        // silent: if endpoint fails, don't block the start page
      }
    };

    redirectIfActive();
    return () => { cancelled = true; };
  }, [navigate]);

  const fetchInitial = async () => {
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const [prods, ings, cfgRes] = await Promise.all([
        logService.getProducts(),
        ingredientService.getIngredients(),
        bakingCcpService.getConfig().catch(() => null),
      ]);
      setProducts(prods || []);
      setAllIngredients(ings || []);
      setFlowType(cfgRes?.config?.flowType || 'baking');
      setForm(f => ({
        ...f,
        lotCode: f.lotCode || generateLotCode(lotCodeFormat),
        productionStartTime: f.productionStartTime || nowLocalTimeHHMM(),
      }));
    } catch (e) {
      console.error(e);
      setError(t('Failed to load products/ingredients.', 'Error al cargar productos/ingredientes.', lang));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Product picker
  // Only products matching the company's current CCP flow are selectable — prevents starting a
  // baked product (e.g. pizza) under the pasteurization flow or vice versa. Products default to
  // 'baking' if never set, matching Product.productionFlowType's DB default.
  const availableProducts = useMemo(() => {
    return products.filter(p => (p.productionFlowType || 'baking') === flowType);
  }, [products, flowType]);

  const handleProductInputChange = (idx, value) => {
    const clone = [...entries];
    clone[idx].productName = value;
    clone[idx].productId = '';
    setEntries(clone);

    if (value.length > 0) {
      const suggestions = availableProducts.filter(p =>
        (p.name || '').toLowerCase().includes(value.toLowerCase())
      );
      setProductSuggestions(ps => ({ ...ps, [idx]: suggestions }));
    } else {
      setProductSuggestions(ps => ({ ...ps, [idx]: [] }));
    }
  };

  const handleSelectProduct = (idx, product) => {
    const clone = [...entries];
    clone[idx].productName = product.name;
    clone[idx].productId = product.id;
    setEntries(clone);
    setProductSuggestions(ps => ({ ...ps, [idx]: [] }));
  };

  const handleShowAllProducts = (idx) => {
    setProductSuggestions(ps => ({ ...ps, [idx]: availableProducts }));
  };

  const addEntry = () => setEntries(e => [...e, { productId: '', productName: '' }]);
  const removeEntry = (idx) => setEntries(e => e.filter((_, i) => i !== idx));

  // Prefill ingredient list from selected products (dedup)
  useEffect(() => {
    const updateIngredientEntries = async () => {
      try {
        const selectedProductIds = entries
          .map(e => e.productId)
          .filter(Boolean)
          .map(x => Number(x));

        if (!selectedProductIds.length) {
          setIngredientEntries([]);
          return;
        }

        const allIngredientIds = new Set();

        for (const pid of selectedProductIds) {
          let recipe = cachedRecipesRef.current[pid];
          if (!recipe) {
            try {
              const raw = await productService.getProductRecipe(pid);
              recipe = raw.allIngredients || (Array.isArray(raw) ? raw : (raw.ingredients || []));
              cachedRecipesRef.current[pid] = recipe;
            } catch (e) {
              console.error('Error fetching recipe for productId', pid, e);
              continue;
            }
          }
          (recipe || []).forEach(item => allIngredientIds.add(Number(item?.ingredientId ?? item)));
        }

        setIngredientEntries(prev => {
          const next = Array.from(allIngredientIds)
            .filter(Boolean)
            .sort((a, b) => {
              const an = ingredientById.get(a)?.name || '';
              const bn = ingredientById.get(b)?.name || '';
              return an.localeCompare(bn);
            })
            .map(id => {
              const existing = prev.find(x => Number(x.ingredientId) === Number(id));
              return { ingredientId: id, ingredientLotCode: existing?.ingredientLotCode || '' };
            });

          return next;
        });
      } catch (e) {
        console.error(e);
        setError(t('Failed to build ingredient list from selected products.', 'Error al construir la lista de ingredientes de los productos seleccionados.', lang));
      }
    };

    updateIngredientEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, ingredientById]);

  const selectedProductIds = useMemo(() => {
    return entries.map(e => e.productId).filter(Boolean).map(x => Number(x));
  }, [entries]);

  // True when ALL selected products are marked no-bake — suppresses oven temp field
  const isNoBakeRun = useMemo(() => {
    if (!selectedProductIds.length) return false;
    return selectedProductIds.every(id => {
      const p = products.find(p => Number(p.id) === Number(id));
      return p?.noBake === true;
    });
  }, [selectedProductIds, products]);

  const missingLots = useMemo(() => {
    return (ingredientEntries || []).filter(x => !String(x.ingredientLotCode || '').trim());
  }, [ingredientEntries]);

  const canStart = useMemo(() => {
    if (!form.lotCode || String(form.lotCode).trim() === '') return false;
    if (!form.productionDate) return false;
    if (!form.productionStartTime) return false;
    if (!selectedProductIds.length) return false;

    if (!isNoBakeRun) {
      const oven = Number(form.ovenTempStartF);
      if (!Number.isFinite(oven) || oven <= 0) return false;
    }

    const startISO = combineDateAndTimeToISO(form.productionDate, form.productionStartTime);
    if (!startISO) return false;

    return true;
  }, [form.lotCode, form.productionDate, form.productionStartTime, form.ovenTempStartF, selectedProductIds.length, isNoBakeRun]);

  const doStart = async () => {
    setStarting(true);
    setError('');
    setNotice('');
    try {
      const productionStartAt = combineDateAndTimeToISO(form.productionDate, form.productionStartTime);
      if (!productionStartAt) {
        setError(t('Invalid production start time.', 'Hora de inicio de producción inválida.', lang));
        return;
      }

      const payload = {
        lotCode: String(form.lotCode).trim(),
        productionDate: form.productionDate || null,
        productionStartAt,
        productIds: selectedProductIds,
        ovenTempStartF: isNoBakeRun ? null : Number(form.ovenTempStartF),
        ingredientLots: (ingredientEntries || []).map(x => ({
          ingredientId: Number(x.ingredientId),
          ingredientLotCode: String(x.ingredientLotCode || '').trim() || null,
        })),
      };

      const res = await bakingCcpService.startRun(payload);
      const runId = res?.run?.id;

      if (!runId) {
        setError(t('Start succeeded but run id was not returned.', 'El inicio fue exitoso pero no se devolvió el ID de la ejecución.', lang));
        return;
      }

      setNotice(t('Production started.', 'Producción iniciada.', lang));
      navigate(`/ccp/baking/live/${runId}`, { replace: true });
    } catch (e) {
      console.error(e);

      const apiMsg = e?.response?.data?.message || '';
      const existingRunId = e?.response?.data?.runId;

      if (existingRunId) {
        let runStatus = '';
        try {
          const runRes = await bakingCcpService.getRun(existingRunId);
          runStatus = runRes?.run?.status || '';
        } catch (err2) {
          console.error('Failed to fetch existing run status', err2);
        }

        setExistingLotInfo({
          message: apiMsg || t('This lot code has already been used.', 'Este código de lote ya fue utilizado.', lang),
          runId: existingRunId,
          runStatus,
          lotCode: String(form.lotCode || '').trim(),
        });
        setShowExistingLotModal(true);
        setError('');
        return;
      }

      const msg = apiMsg || t('Failed to start production run.', 'Error al iniciar la ejecución de producción.', lang);
      setError(msg);
    } finally {
      setStarting(false);
      setShowMissingLotsModal(false);
    }
  };

  const handleStart = async () => {
    setError('');
    setNotice('');

    if (missingLots.length > 0) {
      setShowMissingLotsModal(true);
      return;
    }

    // Check pre-operation gate — creates instances only at this moment
    try {
      const gate = await sqfService.prepareProductionGate();
      if (gate.gated) {
        setGateBlocking(gate.pending);
        return;
      }
    } catch (e) {
      console.warn('Gate check failed, proceeding anyway:', e);
    }

    await doStart();
  };

  const regenerateLotCode = () => setForm(f => ({ ...f, lotCode: generateLotCode(lotCodeFormat) }));

  const ingredientName = (ingredientId) =>
    ingredientById.get(Number(ingredientId))?.name || `Ingredient #${ingredientId}`;

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <h4 className="mb-0">{t(L.pageTitleStart.en, L.pageTitleStart.es, lang)}</h4>
            <div className="text-muted" style={{ fontSize: 12 }}>
              {t('Create the lot (Batch) and start a live CCP run (1 run = 1 batch).', 'Crea el lote (Batch) e inicia una ejecución CCP en vivo (1 ejecución = 1 lote).', lang)}
            </div>
          </div>

          <div className="d-flex gap-2">
            <Link className="btn btn-outline-secondary" to="/traceability">{t('Home', 'Inicio', lang)}</Link>
            <Link className="btn btn-outline-secondary" to="/ccp/baking/config">{t('Config', 'Configuración', lang)}</Link>
            <button className="btn btn-outline-primary" onClick={fetchInitial} disabled={loading || starting}>
              {loading ? t('Loading…', 'Cargando…', lang) : t('Refresh', 'Actualizar', lang)}
            </button>
          </div>
        </div>

        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        {notice && <div className="alert alert-success mt-3 mb-0">{notice}</div>}

        {/* Missing lots modal */}
        {showMissingLotsModal && (
          <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" style={{ fontWeight: 800 }}>
                      {t('Ingredient lot codes are missing', 'Faltan códigos de lote de ingredientes', lang)} ({missingLots.length})
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setShowMissingLotsModal(false)}
                      disabled={starting}
                    />
                  </div>

                  <div className="modal-body">
                    <div className="alert alert-warning mb-3" style={{ fontSize: 13 }}>
                      {t(
                        'You can start production, but this will be flagged for traceability completion before QA verification.',
                        'Puede iniciar la producción, pero esto será marcado para completar trazabilidad antes de la verificación QA.',
                        lang
                      )}
                    </div>

                    <div style={{ fontSize: 13 }}>
                      {t('Missing (showing up to 25):', 'Faltantes (mostrando hasta 25):', lang)}
                      <ul className="mt-2 mb-0">
                        {missingLots.slice(0, 25).map((i) => (
                          <li key={i.ingredientId}>{ingredientName(i.ingredientId)}</li>
                        ))}
                        {missingLots.length > 25 && <li>…{t('and', 'y', lang)} {missingLots.length - 25} {t('more', 'más', lang)}</li>}
                      </ul>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowMissingLotsModal(false)}
                      disabled={starting}
                    >
                      {t('Go Back', 'Volver', lang)}
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={doStart}
                      disabled={starting}
                    >
                      {starting ? t('Starting…', 'Iniciando…', lang) : t('Start Anyway', 'Iniciar de Todos Modos', lang)}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show" />
          </>
        )}

        <div className="row mt-3 g-3">
          <div className="col-12 col-lg-6">
            <label className="form-label mb-1">{t('Lot Code (Auto)', 'Código de Lote (Auto)', lang)}</label>
            <div className="input-group">
              <input
                className="form-control"
                value={form.lotCode}
                onChange={(e) => setForm((f) => ({ ...f, lotCode: e.target.value }))}
              />
              <button type="button" className="btn btn-outline-secondary" onClick={regenerateLotCode} disabled={starting || loading}>
                {t('Regenerate', 'Regenerar', lang)}
              </button>
            </div>
          </div>

          <div className="col-12 col-lg-3">
            <label className="form-label mb-1">{t('Production Date', 'Fecha de Producción', lang)}</label>
            <input
              type="date"
              className="form-control"
              value={form.productionDate}
              onChange={(e) => setForm((f) => ({ ...f, productionDate: e.target.value }))}
            />
          </div>

          <div className="col-12 col-lg-3">
            <label className="form-label mb-1">{t('Production Start Time', 'Hora de Inicio de Producción', lang)}</label>
            <input
              type="time"
              className="form-control"
              value={form.productionStartTime}
              onChange={(e) => setForm((f) => ({ ...f, productionStartTime: e.target.value }))}
            />
            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
              {t('Default is now. Adjust if production started earlier.', 'Por defecto es ahora. Ajuste si la producción empezó antes.', lang)}
            </div>
          </div>

          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <div style={{ fontWeight: 800, fontSize: 16 }}>{t('Products (Select 1+)', 'Productos (Seleccionar 1+)', lang)}</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {t(
                    'Add multiple products if needed. Ingredients will auto-populate from all selected recipes.',
                    'Agregue múltiples productos si es necesario. Los ingredientes se completarán automáticamente con todas las recetas seleccionadas.',
                    lang
                  )}
                  {' '}{t(`Only showing products set to the ${L.stage1Action.en} process — change this per-product in Products settings.`, `Solo se muestran productos configurados con el proceso de ${L.stage1Action.es} — cambie esto por producto en Configuración de Productos.`, lang)}
                </div>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={addEntry} disabled={starting || loading}>
                + {t('Add Product', 'Agregar Producto', lang)}
              </button>
            </div>

            {entries.map((entry, idx) => (
              <div key={idx} className="row mt-2 g-2 align-items-end">
                <div className="col-12 col-lg-8 position-relative">
                  <label className="form-label mb-1">{t('Product', 'Producto', lang)}</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      value={entry.productName}
                      onChange={(e) => handleProductInputChange(idx, e.target.value)}
                      placeholder={t('Type to search product…', 'Escribir para buscar producto…', lang)}
                      style={{ fontSize: 18, fontWeight: 700 }}
                    />
                    <button type="button" className="btn btn-outline-secondary" onClick={() => handleShowAllProducts(idx)}>
                      ▾
                    </button>
                  </div>

                  {productSuggestions[idx]?.length > 0 && (
                    <ul
                      className="list-group position-absolute w-100 mt-1"
                      style={{
                        maxHeight: '250px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      }}
                    >
                      {productSuggestions[idx].map((p) => (
                        <li
                          key={p.id}
                          className="list-group-item"
                          style={{ cursor: 'pointer' }}
                          onMouseDown={() => handleSelectProduct(idx, p)}
                        >
                          {p.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="col-12 col-lg-4 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-danger w-100"
                    onClick={() => removeEntry(idx)}
                    disabled={entries.length === 1 || starting || loading}
                  >
                    {t('Remove', 'Eliminar', lang)}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {isNoBakeRun ? (
            <div className="col-12">
              <div className="alert alert-info mb-0 py-2" style={{ fontSize: 13 }}>
                🧊 <strong>{t('No-Bake Production', 'Producción Sin Horneado', lang)}</strong> — {t('all selected products are marked no-bake. Oven temperature is not required.', 'todos los productos seleccionados son sin horneado. No se requiere temperatura del horno.', lang)}
              </div>
            </div>
          ) : (
            <div className="col-12 col-lg-6">
              <label className="form-label mb-1">{t(L.vesselTempLabel.en, L.vesselTempLabel.es, lang)}</label>
              <input
                type="number"
                className="form-control form-control-lg"
                value={form.ovenTempStartF}
                onChange={(e) => setForm((f) => ({ ...f, ovenTempStartF: e.target.value }))}
                placeholder={L.vesselTempPlaceholder}
                style={{ fontSize: 18, fontWeight: 700 }}
                required
              />
            </div>
          )}

          <div className="col-12">
            <div style={{ fontWeight: 800, fontSize: 16 }}>{t('Ingredient Lot Codes', 'Códigos de Lote de Ingredientes', lang)}</div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              {t('Auto-built from selected product recipes. Deduped.', 'Generado automáticamente de las recetas de productos seleccionados. Sin duplicados.', lang)}
            </div>

            {ingredientEntries.length > 0 ? (
              <div className="table-responsive mt-2">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: '45%' }}>{t('Ingredient', 'Ingrediente', lang)}</th>
                      <th>{t('Lot Code', 'Código de Lote', lang)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ingredientEntries.map((ing) => {
                      const isMissing = !String(ing.ingredientLotCode || '').trim();
                      return (
                        <tr key={ing.ingredientId}>
                          <td>
                            <div style={{ fontWeight: 700 }}>{ingredientName(ing.ingredientId)}</div>
                            {isMissing && <div className="text-danger" style={{ fontSize: 12 }}>{t('Missing', 'Faltante', lang)}</div>}
                          </td>
                          <td>
                            <input
                              className="form-control form-control-lg"
                              value={ing.ingredientLotCode}
                              onChange={(e) => {
                                const v = e.target.value;
                                setIngredientEntries(prev =>
                                  prev.map(x =>
                                    Number(x.ingredientId) === Number(ing.ingredientId)
                                      ? { ...x, ingredientLotCode: v }
                                      : x
                                  )
                                );
                              }}
                              placeholder={t('Enter / scan lot code', 'Ingresar / escanear código de lote', lang)}
                              style={{ fontSize: 18, fontWeight: 700 }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted mt-2" style={{ fontSize: 13 }}>
                {t('No ingredients loaded.', 'No se cargaron ingredientes.', lang)}
              </div>
            )}
          </div>

          <div className="col-12">
            <button
              className="btn btn-success btn-lg w-100"
              style={{ fontSize: 22, fontWeight: 800, padding: '14px 16px' }}
              onClick={handleStart}
              disabled={!canStart || starting || loading}
            >
              {starting ? t('Starting…', 'Iniciando…', lang) : t('START PRODUCTION', 'INICIAR PRODUCCIÓN', lang)}
            </button>
          </div>
        </div>
      </div>

      {/* EXISTING LOT MODAL */}
      {showExistingLotModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('Lot code already used', 'Código de lote ya utilizado', lang)}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowExistingLotModal(false)}
                />
              </div>

              <div className="modal-body">
                <div className="alert alert-warning mb-2" style={{ fontWeight: 800 }}>
                  {existingLotInfo.message || t('This lot code has already been used.', 'Este código de lote ya fue utilizado.', lang)}
                </div>

                <div className="text-muted" style={{ fontSize: 13 }}>
                  {t('Lot', 'Lote', lang)}: <span style={{ fontWeight: 900 }}>{existingLotInfo.lotCode || '—'}</span>
                </div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  {t('Run ID', 'ID de Ejecución', lang)}: <span style={{ fontWeight: 900 }}>{existingLotInfo.runId || '—'}</span>
                </div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  {t('Status', 'Estado', lang)}: <span style={{ fontWeight: 900 }}>{existingLotInfo.runStatus || t('Unknown', 'Desconocido', lang)}</span>
                </div>

                <div className="mt-3" style={{ fontSize: 13 }}>
                  {isRunInProgress(existingLotInfo.runStatus)
                    ? t('Production is still in progress. You can open the live run.', 'La producción aún está en progreso. Puede abrir la ejecución en vivo.', lang)
                    : t('This run has been completed. Open the SQF report to review or verify it.', 'Esta ejecución ha sido completada. Abra el informe SQF para revisarlo o verificarlo.', lang)}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowExistingLotModal(false)}
                >
                  {t('Close', 'Cerrar', lang)}
                </button>

                {isRunInProgress(existingLotInfo.runStatus) ? (
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setShowExistingLotModal(false);
                      navigate(`/ccp/baking/live/${existingLotInfo.runId}`);
                    }}
                    disabled={!existingLotInfo.runId}
                  >
                    {t('Go to Live Production', 'Ir a Producción en Vivo', lang)}
                  </button>
                ) : (
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      setShowExistingLotModal(false);
                      navigate(`/ccp/baking/verify/${existingLotInfo.runId}`);
                    }}
                    disabled={!existingLotInfo.runId}
                  >
                    {t('Open SQF Report', 'Abrir Informe SQF', lang)}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PRE-OPERATION GATE MODAL */}
      {gateBlocking && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header border-danger">
                <h5 className="modal-title text-danger">
                  ⛔ {t('Pre-operation checklist required', 'Lista de verificación pre-operación requerida', lang)}
                </h5>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: 14 }}>
                  {t(
                    'The following checklist(s) must be completed before production can start:',
                    'La(s) siguiente(s) lista(s) de verificación deben completarse antes de iniciar la producción:',
                    lang
                  )}
                </p>
                <ul className="mb-0">
                  {gateBlocking.map(p => (
                    <li key={p.templateId}>
                      <a href={`/sqf/fill/${p.instanceId}`} className="fw-semibold">
                        {p.templateName}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setGateBlocking(null)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <a href={`/sqf/fill/${gateBlocking[0]?.instanceId}`} className="btn btn-danger">
                  {t('Complete Now →', 'Completar Ahora →', lang)}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
